import { Icon } from "@mui/material";
import {
  DashboardOutlined,
  ConfirmationNumberOutlined,
  WorkOutlineOutlined,
  PeopleOutlineOutlined,
  SettingsSuggestOutlined,
  AccountBalanceWalletOutlined,
  AccountCircleOutlined,
  BarChartOutlined,
  LockResetOutlined,
  FolderOutlined,
  AccountBalanceOutlined,
  BusinessOutlined,
  DescriptionOutlined,
  SmartToyOutlined,
} from "@mui/icons-material";

/**
 * Customer Route Configuration
 *
 * Menu is organized into sections:
 * - Dashboard
 * - EMPLOYEE SERVICES (Tickets, Workflow Requests, Employees)
 * - ORGANIZATION SERVICES (Organization, Document Services, Tax Services)
 * - ACCOUNT & SETTINGS (Wallet, Account, Configuration, Reports)
 */
const customerRoute = [
  // ═══════════════════════════════════════════════════════════════════════════
  // AI ASSISTANT
  // ═══════════════════════════════════════════════════════════════════════════
  {
    type: "collapse",
    name: "AI Assistant",
    key: "CST-Agent",
    noCollapse: true,
    icon: <SmartToyOutlined />,
    collapse: [
      {
        name: "AI Assistant",
        key: "chat",
        route: "CST-Agent/chat",
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // DASHBOARD
  // ═══════════════════════════════════════════════════════════════════════════
  {
    type: "collapse",
    name: "Dashboards",
    key: "cst",
    noCollapse: true,
    icon: <DashboardOutlined />,
    collapse: [
      {
        name: "Dashboard",
        key: "dash",
        route: "cst/dash",
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // EMPLOYEE SERVICES SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    type: "title",
    title: "EMPLOYEE SERVICES",
    key: "title-employee-services"
  },
  {
    type: "collapse",
    name: "Tickets",
    key: "ADM-Ticket",
    noCollapse: true,
    icon: <ConfirmationNumberOutlined />,
    collapse: [
      {
        name: "My Tickets",
        key: "pending-tickets",
        route: "ADM-Ticket/pending-tickets",
        description: "View and manage employee service tickets"
      },
      {
        name: "New Ticket",
        key: "new-ticket",
        route: "ADM-Ticket/new-ticket",
        description: "Create a new employee service request"
      },
    ],
  },
  {
    type: "collapse",
    name: "Workflow Requests",
    key: "CST-Workflow",
    icon: <WorkOutlineOutlined />,
    noCollapse: true,
    collapse: [
      {
        name: "My Requests",
        key: "requests",
        route: "CST-Workflow/requests",
        description: "View employee workflow requests"
      },
      {
        name: "New Request",
        key: "new-request",
        route: "CST-Workflow/new-request",
        description: "Create a new employee workflow request"
      },
    ],
  },
  {
    type: "collapse",
    name: "Employees",
    key: "CST-Employees",
    icon: <PeopleOutlineOutlined />,
    noCollapse: true,
    collapse: [
      {
        name: "Employee List",
        key: "list",
        route: "CST-Employees/list",
        description: "Manage your organization's employees"
      },
      {
        name: "Add Employee",
        key: "create",
        route: "CST-Employees/create",
        description: "Add a new employee to your organization"
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ORGANIZATION SERVICES SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    type: "title",
    title: "ORGANIZATION SERVICES",
    key: "title-organization-services"
  },
  {
    type: "collapse",
    name: "Organization",
    key: "CST-Organization",
    icon: <BusinessOutlined />,
    noCollapse: true,
    collapse: [
      {
        name: "Company Documents",
        key: "documents",
        route: "CST-OrgDocuments/list",
        description: "Manage company documents (Trade License, Tax Certificates, etc.)"
      },
      {
        name: "Required Documents",
        key: "required",
        route: "CST-OrgDocuments/required",
        description: "View compliance status and required documents"
      },
    ],
  },
  {
    type: "collapse",
    name: "Stakeholders",
    key: "CST-Stakeholders",
    icon: <AccountCircleOutlined />,
    noCollapse: true,
    collapse: [
      {
        name: "Stakeholder List",
        key: "list",
        route: "CST-Stakeholders/list",
        description: "Manage organization stakeholders (owners, partners, directors)"
      },
      {
        name: "Add Stakeholder",
        key: "create",
        route: "CST-Stakeholders/create",
        description: "Add a new organization stakeholder"
      },
    ],
  },
  {
    type: "collapse",
    name: "Document Services",
    key: "CST-DocServices",
    icon: <DescriptionOutlined />,
    noCollapse: true,
    collapse: [
      {
        name: "My Requests",
        key: "requests",
        route: "CST-DocServices/requests",
        description: "View your organization service requests"
      },
      {
        name: "Request Service",
        key: "new-request",
        route: "CST-DocServices/new-request",
        description: "Create a new organization service request"
      },
    ],
  },
  {
    type: "collapse",
    name: "Tax Services",
    key: "CST-TaxServices",
    icon: <AccountBalanceOutlined />,
    noCollapse: false,
    collapse: [
      {
        name: "My Requests",
        key: "requests",
        route: "CST-TaxServices/requests",
        description: "View your tax service requests and their status"
      },
      {
        name: "Tax Status",
        key: "status",
        route: "CST-TaxServices/status",
        description: "View tax profile, filing status, and upcoming deadlines"
      },
      {
        name: "Request Service",
        key: "list",
        route: "CST-TaxServices/list",
        description: "UAE Corporate Tax and VAT registration, filing, and compliance services"
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ACCOUNT & SETTINGS SECTION
  // ═══════════════════════════════════════════════════════════════════════════
  {
    type: "title",
    title: "ACCOUNT & SETTINGS",
    key: "title-account-settings"
  },
  {
    type: "collapse",
    name: "Wallet",
    key: "wallet",
    icon: <AccountBalanceWalletOutlined />,
    noCollapse: true,
    collapse: [
      {
        name: "My Payment Methods",
        key: "customer-payment-methods",
        route: "wallet/customer-payment-methods",
      },
    ],
  },
  {
    type: "collapse",
    name: "Account",
    key: "account",
    icon: <AccountCircleOutlined />,
    noCollapse: true,
    collapse: [
      /* {
        name: "Account Management",
        key: "account-management",
        route: "account/management",
      }, */
      {
        name: "Payments",
        key: "payments",
        route: "account/payments",
      },
      /* {
        name: "Legacy Invoices",
        key: "my-invoices",
        route: "account/my-invoices",
      }, */
      {
        name: "Statements",
        key: "statements",
        route: "account/statements",
      },
    ],
  },
  {
    type: "collapse",
    name: "Configuration",
    key: "cst-configuration",
    noCollapse: true,
    icon: <SettingsSuggestOutlined />,
    collapse: [
      {
        name: "Company Profile",
        key: "company-profile",
        route: "CST-Organization/profile",
        description: "View and edit company profile and business category"
      },
      {
        name: "My Profile",
        key: "my-profile",
        route: "cst/my-profile",
      },
      {
        name: "Organisation Configuration",
        key: "configuration",
        route: "cst/configuration",
      },
      {
        name: "Subscription",
        key: "subscription",
        route: "cst/subscription",
      },
      {
        name: "Reset Password",
        key: "reset_password",
        route: "ADM-APPCONFIG/reset_password",
      },
    ],
  },
  {
    type: "collapse",
    name: "Reports",
    key: "CST-Reports",
    icon: <BarChartOutlined />,
    noCollapse: true,
    collapse: [
      {
        name: "Expiring Documents",
        key: "expiring-documents",
        route: "CST-Dashboard/reports",
      },
      {
        name: "Missing Documents",
        key: "missing-documents",
        route: "CST-Dashboard/missing-documents",
      },
    ],
  },
];

export default customerRoute;
